import React, { useState, useEffect, useRef, Fragment } from "react";
import * as httpService from "../../services/httpService";
import { Employee_Api_EndPoints } from "../../global/end-points";
import * as userService from "../../utils/userService";
import { Link } from "react-router-dom";
import LoadingSpinner from "../common/loadingSpinner";
import { useTranslation } from "react-i18next";
import useActivityLogger from "../../utils/useActivityLogger";
import { useNavigate } from "react-router-dom"; // <-- import this

function Dashboard() {
  const { t } = useTranslation();
  const [isLoad, setIsLoad] = useState(false);
  const navigate = useNavigate(); // <-- initialize navigate
  const didMount = useRef(false);
  const [breadPoints, setBreadPoints] = useState({
    rafilTarget: 0,
    breadPointsPerTrans: 0,
    earnedPoints: 0,
    totalPay: 0,
  });
  const [isPayPeriodCompleted, setIsPayPeriodCompleted] = useState(false);
  const { totalPay } = breadPoints;

  // ✅ detect if OBO login
  const isOBO = !!sessionStorage.getItem("OnBehalfUserId");

  // ✅ use logger
  const { logClick } = useActivityLogger({ moduleCode: "DPY" });



  useEffect(() => {
    if (!didMount.current) {
      // ✅ Wait a brief moment after login to ensure sessionStorage is set
      // This handles cases where component loads before login completes
      const checkAndLoad = () => {
        const userId = sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");
        if (userId) {
          get_target_earned_points();
        } else {
          // Retry after a short delay if userId not available yet
          setTimeout(() => {
            const retryUserId = sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId");
            if (retryUserId) {
              get_target_earned_points();
            } else {
              // Still no userId - set defaults and show page
              setBreadPoints({
                rafilTarget: 0,
                breadPointsPerTrans: 0,
                earnedPoints: 0,
                totalPay: 0,
              });
              setIsLoad(true);
            }
          }, 500); // 500ms retry delay
        }
      };
      
      checkAndLoad();
    }
    didMount.current = true;

    // ✅ intercept back button for OBO users
    const handleBack = (event) => {
      if (isOBO) {
        event.preventDefault();
        navigate("/logout", { replace: true }); // 🔹 go to Logout screen
      }
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  const get_target_earned_points = () => {
    // ✅ decide which user to use
    const userId = Number(
      sessionStorage.getItem("OnBehalfUserId") || sessionStorage.getItem("UserId")
    );
    const phoneNumber =
      sessionStorage.getItem("OnBehalfPhoneNumber") ||
      sessionStorage.getItem("phoneNumber") ||
      "";

    if (userId) {
      const body = {
        UserId: userId,
        PhoneNumber: phoneNumber, // <-- now included if API expects it
        LocationId: 1,
      };

      // ✅ Safety timeout to ensure loading state is always set (prevents infinite loading)
      // Reduced to 10 seconds for better UX - if API takes longer, show default values
      const timeoutId = setTimeout(() => {
        console.warn("API call timeout - setting default values to prevent infinite loading");
        setBreadPoints({
          rafilTarget: 0,
          breadPointsPerTrans: 0,
          earnedPoints: 0,
          totalPay: 0,
        });
        setIsPayPeriodCompleted(true);
        setIsLoad(true);
      }, 10000); // 10 second timeout - more reasonable for user experience

      httpService.Get(Employee_Api_EndPoints.EARNED_POINTS, body).then((resp) => {
        // ✅ Clear timeout since we got a response
        clearTimeout(timeoutId);
        
        // ✅ Safety check: if resp is null/undefined, treat as error
        if (!resp) {
          console.warn("API returned null/undefined response - setting defaults");
          setBreadPoints({
            rafilTarget: 0,
            breadPointsPerTrans: 0,
            earnedPoints: 0,
            totalPay: 0,
          });
          setIsPayPeriodCompleted(false);
          setIsLoad(true);
          return;
        }
        
        // ✅ Handle 500 errors and other error responses gracefully
        // When pay period is completed, backend may return 500 - display 0 values instead
        // httpService returns error objects directly in .then(), so check structure:
        // Success: { status: 200, data: {...} }
        // Error: { response: { status: 500 }, code: '...' } or { response: {...} }
        
        // ✅ More lenient check: if we have data and status 200, treat as success
        // Only treat as error if we have explicit error structure (response property)
        const hasExplicitError = resp && resp.response;
        const hasValidData = resp && resp.data && typeof resp.data === 'object';
        const hasValidStatus = resp && resp.status === 200;
        
        try {
          if (hasValidStatus && hasValidData && !hasExplicitError) {
            // ✅ Success: Normal data received
            // Ensure data has expected structure, otherwise use defaults
            const data = resp.data || {};
            setBreadPoints({
              rafilTarget: Number(data.rafilTarget) || 0,
              breadPointsPerTrans: Number(data.breadPointsPerTrans) || 0,
              earnedPoints: Number(data.earnedPoints) || 0,
              totalPay: Number(data.totalPay) || 0,
            });
            setIsPayPeriodCompleted(false);
          } else {
            // ✅ Error response (500, network error, etc.) - set default values
            // This handles completed pay period scenario gracefully
            const errorStatus = resp?.response?.status;
            const is500Error = errorStatus === 500;
            setIsPayPeriodCompleted(is500Error); // Track if pay period is completed
            
            setBreadPoints({
              rafilTarget: 0,
              breadPointsPerTrans: 0,
              earnedPoints: 0,
              totalPay: 0,
            });
          }
        } catch (err) {
          // ✅ Handle any unexpected errors in processing
          console.error("Error processing API response:", err);
          setBreadPoints({
            rafilTarget: 0,
            breadPointsPerTrans: 0,
            earnedPoints: 0,
            totalPay: 0,
          });
          setIsPayPeriodCompleted(false);
        }
        // ✅ ALWAYS set loading to true to stop the spinner, even on errors
        setIsLoad(true);
      }).catch((error) => {
        // ✅ Clear timeout since we got a response (even if it's an error)
        clearTimeout(timeoutId);
        
        // ✅ Additional catch for any unhandled promise rejections
        // This ensures the page always loads, even on unexpected errors
        console.error("Error fetching earned points:", error);
        
        // Check if this is a 500 error (completed pay period scenario)
        const is500Error = error && error.response && error.response.status === 500;
        setIsPayPeriodCompleted(is500Error);
        
        setBreadPoints({
          rafilTarget: 0,
          breadPointsPerTrans: 0,
          earnedPoints: 0,
          totalPay: 0,
        });
        // ✅ ALWAYS set loading to true to stop the spinner
        setIsLoad(true);
      });
    } else {
      // ✅ No userId - set default values and show page (don't keep loading)
      setBreadPoints({
        rafilTarget: 0,
        breadPointsPerTrans: 0,
        earnedPoints: 0,
        totalPay: 0,
      });
      setIsLoad(true); // Set to true to show the page, not keep loading
      userService.ClearSession();
    }
  };

  return (
    <Fragment>
      {isLoad && (
        <div className="dashboard-container">
          {/* summary cards row start */}
          <div className="dashboard-card-row">
            <div className="summary-card">
              <div className="summary-card-inner">
                <div className="summary-card-details">
                  <h2 className="card-heading">{t("summary")}</h2>
                  <h2 className="wages-heading">{t("availableWagestoAccess")}</h2>
                  {totalPay === 0 ? (
                  <div className="zero-wages-container">
                      {/* ✅ Show $0 in the same orange style as other amounts, per requirement */}
                      <span className="wage-amount">
                        $ {totalPay.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {isPayPeriodCompleted ? (
                        <div className="info-message-container">
                          <h3 className="wage-card-number info-message">
                            <i className="fas fa-info-circle" style={{ marginRight: '8px', fontSize: '16px' }}></i>
                            Pay period has been completed. New wages will appear when available.
                          </h3>
                        </div>
                      ) : (
                        <h3 className="wage-card-number">{t("NoEarnedWages")}</h3>
                      )}
                    </div>
                  ) : (
                    <span className="wage-amount">
                      $ {totalPay.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                  <div className="card-holder-details-provider"></div>
                </div>
              </div>
            </div>

            {/* ✅ Instant Payout button with logging */}
            <div className="get-your-dough-card">
              <div className="dough-card-inner">
                <Link
                  to="/doughsummary"
                  className="get-your-dough-btn"
                  onClick={logClick(
                    isOBO ? "OBO.Btn.InstantPayout" : "Normal.Btn.InstantPayout",
                    isOBO
                      ? "OBO-Instant Payout Button Click"
                      : "Instant Payout Button Click"
                  )}
                >
                  {t("GetDh")}
                </Link>
              </div>
            </div>

          </div>
          {/* summary cards row end */}

          <div className="bread-points-section"></div>
        </div>
      )}
      {!isLoad && <LoadingSpinner />}
    </Fragment>
  );
}

export default Dashboard;
